import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import isEqual from 'lodash/isEqual';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { CardClient, SmartCardProvider } from '@atlaskit/link-provider';
import { ReactRenderer } from '@atlaskit/renderer';
import { FireUIAnalytics } from '@atlassian/analytics-bridge';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { ExperienceSuccess } from '@atlassian/ufo';
import { PackageIntlProvider, withPackageIntlProvider } from '../../intl-provider';
import { useMediaContext } from '../../jira-common/context';
import { useEmitAttachmentMetrics } from '../../jira-common/utils/analytics/attachment';
import { HtmlExtension } from './extensions/HtmlExtension';
import { rendererEventHandlers } from './rendererEventHandlers';
import { RenderQuestion } from './RenderQuestion';
import { IntlViewFormMessages, ViewFormMessage } from './ViewFormMessages.intl';

/**
 * Ths is hardcoded as 'light' everywhere except for `template-preview`.
 * See `/jira/src/packages/proforma/proforma-template-library/src/ui/library/template-preview/index.tsx`.
 */

export var DisplayMode = /*#__PURE__*/function (DisplayMode) {
  /**
   * View: Display the form as a plain document, display all answers are plain text
   */
  DisplayMode["View"] = "view";
  /**
   * Edit: Display the form in edit mode with all input fields
   */
  DisplayMode["Edit"] = "edit";
  /**
   * Preview: Display the form with all input fields disabled
   */
  DisplayMode["Preview"] = "preview";
  /**
   * Translate: Display the form for translating with all input fields disabled
   */
  DisplayMode["Translate"] = "translate";
  return DisplayMode;
}({});
var environment = window.location.hostname === 'localhost' ? 'staging' : undefined; // Make smart links connect to staging when developing locally.
var smartCardClient = new CardClient(environment);
var formExtensionFn = function formExtensionFn(intl, formStoreRef, displayModeRef, blockFormSubmission, linkedFieldNames, QuestionRenderer, questionTypeRendererMap
// eslint-disable-next-line @typescript-eslint/ban-types
) {
  return function (ext) {
    var formStore = formStoreRef.current;
    if (ext.extensionKey === 'question') {
      var _ext$parameters, _ref, _ref2, _formStore$translated;
      var questionId = (_ext$parameters = ext.parameters) === null || _ext$parameters === void 0 ? void 0 : _ext$parameters.id;
      var questionStore = questionId !== undefined && formStore.questions.find(function (question) {
        return question.id === questionId;
      });
      if (!questionStore) {
        // eslint-disable-next-line no-console
        console.error("Could not find the question store with ID ".concat(questionId, ":"), ext);
        return /*#__PURE__*/React.createElement(React.Fragment, null);
      }
      var displayLocale = (_ref = (_ref2 = (_formStore$translated = formStore.translatedLocale) !== null && _formStore$translated !== void 0 ? _formStore$translated : formStore.primaryLocale) !== null && _ref2 !== void 0 ? _ref2 : formStore.language) !== null && _ref !== void 0 ? _ref : intl.locale;
      return /*#__PURE__*/React.createElement(PackageIntlProvider, {
        locale: displayLocale
      }, /*#__PURE__*/React.createElement(RenderQuestion, {
        questionStore: questionStore,
        displayMode: displayModeRef.current,
        formStore: formStore,
        blockFormSubmission: blockFormSubmission,
        linkedFieldNames: linkedFieldNames,
        QuestionRenderer: QuestionRenderer,
        questionTypeRendererMap: questionTypeRendererMap
      }));
    }
    if (ext.extensionKey === 'html') {
      return /*#__PURE__*/React.createElement(HtmlExtension, {
        extensionId: ext.parameters.id,
        htmlContent: ext.parameters.htmlContent || ''
      });
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  };
};
export var isSummaryOnTopLevel = function isSummaryOnTopLevel(linkedSummaryQuestionId, formLayout) {
  var _formLayout$some;
  return (_formLayout$some = formLayout === null || formLayout === void 0 ? void 0 : formLayout.some(function (layout) {
    return layout.content.some(function (content) {
      var _content$attrs;
      return content.type === 'extension' && ((_content$attrs = content.attrs) === null || _content$attrs === void 0 || (_content$attrs = _content$attrs.parameters) === null || _content$attrs === void 0 ? void 0 : _content$attrs.id) === linkedSummaryQuestionId;
    });
  })) !== null && _formLayout$some !== void 0 ? _formLayout$some : false;
};
export var RenderForm = withPackageIntlProvider(observer(function (_ref3) {
  var _formStore$layout, _ref4, _ref5, _formStore$translated2, _formStore$language;
  var formStore = _ref3.formStore,
    showHiddenSections = _ref3.showHiddenSections,
    displayMode = _ref3.displayMode,
    backgroundColor = _ref3.backgroundColor,
    ufoExperience = _ref3.ufoExperience,
    blockFormSubmission = _ref3.blockFormSubmission,
    linkedFieldNames = _ref3.linkedFieldNames,
    QuestionRenderer = _ref3.QuestionRenderer,
    questionTypeRendererMap = _ref3.questionTypeRendererMap,
    nodeComponents = _ref3.nodeComponents;
  var intl = useIntl();
  var formStoreRef = useRef(formStore);
  if (formStoreRef.current !== formStore) {
    formStoreRef.current = formStore;
  }
  var displayModeRef = useRef(displayMode);
  if (displayModeRef.current !== displayMode) {
    displayModeRef.current = displayMode;
  }
  var _useMediaContext = useMediaContext(),
    mediaReadTokenRefresh = _useMediaContext.mediaReadTokenRefresh;
  var _useState = useState(new Set()),
    _useState2 = _slicedToArray(_useState, 2),
    attachmentIdsCache = _useState2[0],
    setAttachmentIdsCache = _useState2[1];
  useEffect(function () {
    var mediaTokenRefreshTimeoutId;
    if (!isEqual(attachmentIdsCache, formStore.attachmentIds)) {
      setAttachmentIdsCache(new Set(_toConsumableArray(formStore.attachmentIds)));
      if (formStore.attachmentIds.size > 0 && mediaReadTokenRefresh) {
        mediaTokenRefreshTimeoutId = setTimeout(function () {
          return mediaReadTokenRefresh();
        }, 1000);
      }
    }
    return function () {
      if (mediaTokenRefreshTimeoutId) {
        clearInterval(mediaTokenRefreshTimeoutId);
      }
    };
    // removing attachmentIdsCache from dependency array as we set it in the useEffect
    // this will immediately trigger the useEffect again which calls the returned cleanup function
    // this will clear the timeout which means the token won't be refreshed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStore.attachmentIds, mediaReadTokenRefresh]);
  useEmitAttachmentMetrics(formStore, displayMode !== DisplayMode.Edit);
  useEffect(function () {
    blockFormSubmission && blockFormSubmission(formStore.isUploadingMedia);
  }, [blockFormSubmission, formStore.isUploadingMedia]);
  var formSections = (_formStore$layout = formStore.layout) === null || _formStore$layout === void 0 ? void 0 : _formStore$layout.map(function (sectionLayout, index) {
    var sectionId = index.toString();
    if (showHiddenSections === undefined && !formStore.visibleSections.includes(sectionId)) {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
    var formSection = /*#__PURE__*/React.createElement(ReactRenderer, {
      key: sectionId,
      document: toJS(sectionLayout),
      extensionHandlers: {
        'com.thinktilt.proforma': formExtensionFn(intl, formStoreRef, displayModeRef, blockFormSubmission, linkedFieldNames, QuestionRenderer, questionTypeRendererMap)
      },
      eventHandlers: rendererEventHandlers,
      nodeComponents: nodeComponents,
      adfStage: "stage0"
    });
    if (showHiddenSections && !formStore.visibleSections.includes(sectionId)) {
      return /*#__PURE__*/React.createElement(SectionWrapperStyles, {
        key: sectionId
      }, /*#__PURE__*/React.createElement("div", null, formSection), /*#__PURE__*/React.createElement(SectionOverlayStyles, {
        hiddenSectionTheme: showHiddenSections
      }, /*#__PURE__*/React.createElement(SectionMessageStyles, {
        hiddenSectionTheme: showHiddenSections
      }, /*#__PURE__*/React.createElement(FormattedMessage, IntlViewFormMessages[ViewFormMessage.HiddenSection]))));
    }
    return formSection;
  });
  var linkedSummaryQuestionId = formStore === null || formStore === void 0 ? void 0 : formStore.questions.find(function (question) {
    return question.formQuestion.jiraField === 'summary';
  });
  var displayLocale = (_ref4 = (_ref5 = (_formStore$translated2 = formStore.translatedLocale) !== null && _formStore$translated2 !== void 0 ? _formStore$translated2 : formStore.primaryLocale) !== null && _ref5 !== void 0 ? _ref5 : formStore.language) !== null && _ref4 !== void 0 ? _ref4 : intl.locale;
  var form = /*#__PURE__*/React.createElement(FormContainer, null, /*#__PURE__*/React.createElement(FormStyles, {
    backgroundColor: backgroundColor
  }, /*#__PURE__*/React.createElement(SmartCardProvider, {
    client: smartCardClient
  }, formSections)), !!ufoExperience && /*#__PURE__*/React.createElement(ExperienceSuccess, {
    experience: ufoExperience
  }), /*#__PURE__*/React.createElement(FireUIAnalytics, {
    actionSubject: ActionSubject.FORM,
    action: Action.VIEWED,
    actionSubjectId: "proformaRenderForm",
    attributes: _objectSpread(_objectSpread({}, formStore.analyticsAttributes), {}, {
      hasLinkedSummary: !!linkedSummaryQuestionId,
      summaryOnTopLevel: linkedSummaryQuestionId ? isSummaryOnTopLevel(linkedSummaryQuestionId.id, formStore === null || formStore === void 0 ? void 0 : formStore.layout) : false,
      translationDetails: {
        legacyLanguage: (_formStore$language = formStore.language) === null || _formStore$language === void 0 ? void 0 : _formStore$language.replace('_', '-'),
        localeMatched: displayLocale === intl.locale,
        primaryLocale: formStore.primaryLocale,
        translated: formStore.translatedLocale !== formStore.primaryLocale,
        translatedLocale: formStore.translatedLocale,
        translationsApplied: formStore.translatedLocale !== undefined,
        userLocale: intl.locale
      }
    })
  }));
  if (displayLocale !== undefined) {
    return /*#__PURE__*/React.createElement(PackageIntlProvider, {
      locale: displayLocale
    }, form);
  }
  return form;
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
var FormContainer = styled.div({
  height: 'auto',
  overflow: 'visible'
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
var FormStyles = styled.div(function (_ref6) {
  var backgroundColor = _ref6.backgroundColor;
  return {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : "var(--ds-surface, white)",
    padding: "var(--ds-space-100, 8px)",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& .ak-renderer-extension-overflow-container': {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
      overflow: 'unset !important'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& .pm-table-container': {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
      zIndex: 'auto !important'
    }
  };
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
var SectionWrapperStyles = styled.div({
  position: 'relative',
  padding: "var(--ds-space-050, 4px)"
});

/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-dynamic-styles */
/* eslint-disable @atlaskit/ui-styling-standard/no-styled */
var SectionOverlayStyles = styled.div(function (_ref7) {
  var hiddenSectionTheme = _ref7.hiddenSectionTheme;
  return {
    height: '100%',
    width: '100%',
    backgroundColor: hiddenSectionTheme === 'dark' ? "var(--ds-background-disabled, rgba(108, 121, 143, 0.1))" : undefined,
    top: 0,
    left: 0,
    position: 'absolute',
    borderRadius: "var(--ds-radius-small, 3px)",
    textAlign: 'center',
    pointerEvents: 'none'
  };
});
var SectionMessageStyles = styled.h4(function (_ref8) {
  var hiddenSectionTheme = _ref8.hiddenSectionTheme;
  return {
    padding: "var(--ds-space-100, 8px)",
    color: hiddenSectionTheme === 'light' ? "var(--ds-text, #091E42)" : "var(--ds-text, #172b4d)",
    backgroundColor: hiddenSectionTheme === 'light' ? "var(--ds-background-accent-gray-subtler, #DFE1E6)" : "var(--ds-surface, white)",
    display: 'inline-block',
    position: 'relative',
    top: "var(--ds-space-100, 8px)",
    borderRadius: "var(--ds-radius-large, 8px)"
  };
});
/* eslint-enable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-enable @atlaskit/ui-styling-standard/no-dynamic-styles */
/* eslint-enable @atlaskit/ui-styling-standard/no-styled */